import { useEffect, useState } from 'react';
import { apiV2, type Prefs } from '../lib/apiV2';
import { getSession, isSignedIn, signOut } from '../lib/auth';
import { suggestDisplayName } from '../lib/hostName';
import { AuthSheet } from './AuthSheet';

/**
 * Host settings (?tab=settings): the name invites say is hosting, the default
 * room code every new night is pre-filled with, and the Discord webhook — moved
 * OUT of the create flow per the PRD (saved once here, fires automatically on
 * every night).
 */
export function Settings() {
  const [signedIn] = useState(isSignedIn());
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [name, setName] = useState('');
  /** True while the name box holds a guess from the email rather than something
   * the host saved. Drives the "check this" hint — and nothing else: the guess
   * is not sent anywhere until Save, exactly like any other unsaved edit. */
  const [nameIsSuggested, setNameIsSuggested] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [webhook, setWebhook] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    apiV2
      .getPrefs()
      .then((p) => {
        setPrefs(p);
        const suggested = suggestDisplayName(p.email ?? getSession()?.email);
        setName(p.displayName || suggested);
        setNameIsSuggested(!p.displayName && Boolean(suggested));
        setRoomCode(p.defaultRoomCode);
        setWebhook(p.discordWebhook ?? '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load settings.'));
  }, [signedIn]);

  if (!signedIn) {
    return <AuthSheet title="Sign in for settings" />;
  }

  const save = async () => {
    const code = roomCode.trim().toUpperCase();
    if (!code) {
      setError('The room code can’t be empty — it’s what every invite points at.');
      return;
    }
    const hook = webhook.trim();
    if (hook && !hook.startsWith('https://discord.com/api/webhooks/')) {
      setError('That doesn’t look like a Discord webhook URL.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await apiV2.setPrefs({
        displayName: name.trim(),
        defaultRoomCode: code,
        discordWebhook: hook || null,
      });
      // Saved is saved: whatever it started as, it is now the host's own word.
      setNameIsSuggested(false);
      setMsg('Saved.');
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="card marquee">
        <h2 className="display">Settings</h2>
        <p className="soft">Set once — every new night starts from these.</p>

        <label>
          Your name <span className="hint">— invites say &ldquo;{name.trim() || 'Someone'} picked&hellip;&rdquo;</span>
        </label>
        <input
          type="text"
          value={name}
          placeholder="e.g. John"
          maxLength={40}
          onChange={(e) => {
            setName(e.target.value);
            setNameIsSuggested(false);
          }}
          disabled={prefs === null}
        />
        <p className="hint-line">
          {nameIsSuggested
            ? 'We guessed this from your email — check it, then Save. Until you do, invites don’t name a host.'
            : 'A text from an unknown number is easy to mistake for spam. A name fixes that. Leave it empty to keep your invites anonymous.'}
        </p>

        <label>
          Default room code <span className="hint">— pre-filled on every night you plan</span>
        </label>
        <input
          type="text"
          className="mono"
          value={roomCode}
          placeholder="XXX-XXXX"
          maxLength={20}
          onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
          disabled={prefs === null}
        />

        <label>
          Discord webhook <span className="hint">(optional — posts the invite + vote pings)</span>
        </label>
        <input
          type="url"
          value={webhook}
          placeholder="https://discord.com/api/webhooks/…"
          onChange={(e) => setWebhook(e.target.value)}
          disabled={prefs === null}
        />
        <p className="hint-line">
          Server Settings → Integrations → Webhooks → Copy URL. No bot, no signup.
        </p>

        {error && <div className="notice error">{error}</div>}
        {msg && <div className="notice success">{msg}</div>}

        <div className="form-actions">
          <button className="btn primary" onClick={save} disabled={saving || prefs === null}>
            {saving ? <span className="spinner" /> : 'Save settings'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="flex-between">
          <div>
            <strong>Signed in</strong>
            <div className="soft">{getSession()?.email}</div>
          </div>
          <button
            className="btn secondary small"
            onClick={() => {
              signOut();
              window.location.href = window.location.pathname;
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
